import React, { useCallback, useState, useEffect, useRef, useMemo } from "react";
import { Box, Container, Typography, Button, Paper, IconButton, CircularProgress, Dialog, DialogTitle, DialogContent, TextField } from "@mui/material";
import { Save as SaveIcon, Close as CloseIcon } from "@mui/icons-material";
import ChatIcon from "../../../assets/images/AgentStar.svg";
import NewChatIcon from "../../../assets/icons/NewChatIcon";
import { useParams, useNavigate } from "react-router-dom";
import ReactFlow, {
  Node,
  Edge,
  addEdge,
  Connection,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  ReactFlowProvider,
  ReactFlowInstance,
  ConnectionMode,
} from "reactflow";
import "reactflow/dist/style.css";
import Breadcrumb from "../../../Shared-ui/SharedComponents/Breadcrumb";
import NodePalette from "./components/NodePalette";
import { TeamNode, LLMNode, CustomPromptNode } from "./components/CustomNodes";
import ResizeObserverErrorBoundary from "./components/ResizeObserverErrorBoundary";
import BaseInstance from "../../../Services/AxiosInstance/BaseInstance";
import { TEAMS, MODELS, AGENT } from "../../../Services/Endpoint";
import { MultiAgent } from "../../../Shared-ui/Types/MultiAgent";
import { Agent } from "../../../Shared-ui/Types/Agent";
import { useDispatch } from "react-redux";
import { StartLoading, StopLoading } from "../../../Shared-ui/Store/LoadingReducer/LoaderActions";
import { ShowError, ShowSuccess } from "../../../Shared-ui/Store/MessageReducer/MsgActions";
import { NotSelected } from "../../../Constant/Setting";
import { GetSession } from "../../../Shared-ui/utils/Auth/AuthHelpers";

// Debounce utility function
const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const initialNodes: Node[] = [];
const initialEdges: Edge[] = [];

const CreateMultiAgent = () => {
  // Suppress ResizeObserver warnings globally
  React.useEffect(() => {
    const originalError = console.error;
    const originalWarn = console.warn;

    console.error = (...args) => {
      const message = args[0];
      if (typeof message === 'string' && message.includes('ResizeObserver loop completed with undelivered notifications')) {
        return; // Suppress this specific warning
      }
      originalError.apply(console, args);
    };

    console.warn = (...args) => {
      const message = args[0];
      if (typeof message === 'string' && message.includes('ResizeObserver loop completed with undelivered notifications')) {
        return; // Suppress this specific warning
      }
      originalWarn.apply(console, args);
    };

    // Also suppress the error globally
    const handleError = (event: ErrorEvent) => {
      if (event.message && event.message.includes('ResizeObserver loop completed with undelivered notifications')) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    };

    window.addEventListener('error', handleError);

    return () => {
      console.error = originalError;
      console.warn = originalWarn;
      window.removeEventListener('error', handleError);
    };
  }, []);

  // Define node types with memoization
  const nodeTypes = useMemo(() => ({
    teamNode: TeamNode,
    llmNode: LLMNode,
    customPromptNode: CustomPromptNode,
  }), []);

  const { teamId } = useParams<{ teamId?: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Debounced node updates to reduce re-renders
  const debouncedSetNodes = useCallback(
    debounce((newNodes: Node[]) => {
      setNodes(newNodes);
    }, 100),
    [setNodes]
  );

  // Debounced edge updates to reduce re-renders
  const debouncedSetEdges = useCallback(
    debounce((newEdges: Edge[]) => {
      setEdges(newEdges);
    }, 100),
    [setEdges]
  );

  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [multiAgentData, setMultiAgentData] = useState<MultiAgent | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Check if target node already has a connection on the specific handle
  const hasExistingConnection = useCallback((targetNodeId: string, targetHandle: string) => {
    return edges.some((edge: any) =>
      edge.target === targetNodeId && edge.targetHandle === targetHandle
    );
  }, [edges]);

  // Connection validation function
  const isValidConnection = useCallback((connection: Connection) => {
    const { source, target, sourceHandle, targetHandle } = connection;

    // Prevent self-connections
    if (source === target) return false;

    // Define single input handles (only one connection allowed)
    const singleInputHandles = ['llm-input', 'prompt-input'];

    // Check if target handle already has a connection
    if (targetHandle && target && singleInputHandles.includes(targetHandle)) {
      if (hasExistingConnection(target, targetHandle)) {
        return false;
      }
    }

    // Define valid connections
    const validConnections = [
      { sourceType: 'llmNode', targetType: 'teamNode', sourceHandle: 'output', targetHandle: 'llm-input' },
      { sourceType: 'customPromptNode', targetType: 'teamNode', sourceHandle: 'output', targetHandle: 'prompt-input' },
    ];

    const sourceNode = nodes.find(n => n.id === source);
    const targetNode = nodes.find(n => n.id === target);

    if (!sourceNode || !targetNode) return false;

    const sourceType = sourceNode.type;
    const targetType = targetNode.type;

    // Check if this is a valid connection
    const isValid = validConnections.some(conn =>
      conn.sourceType === sourceType &&
      conn.targetType === targetType &&
      conn.sourceHandle === sourceHandle &&
      conn.targetHandle === targetHandle
    );

    return isValid;
  }, [nodes, hasExistingConnection]);

  const onConnect = useCallback((params: Connection) => {
    if (isValidConnection(params)) {
      setEdges((eds) => addEdge(params, eds));
    }
  }, [isValidConnection, setEdges]);

  const onDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();

    const nodeTemplate = event.dataTransfer.getData('application/reactflow');
    if (!nodeTemplate || !reactFlowInstance) return;

    try {
      const template = JSON.parse(nodeTemplate);
      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const newNode: Node = {
        id: `${template.category.toLowerCase()}-${Date.now()}`,
        type: getNodeType(template.category),
        position,
        data: {
          label: template.label,
          category: template.category,
          description: template.description,
          // Initialize with empty arrays for team node
          agents: template.category === 'Team' ? [] : undefined,
          // Initialize LLM-specific data
          provider: template.provider || undefined,
          models: template.models || undefined,
          model: template.models && template.models.length > 0 ? template.models[0] : undefined,
          apiKey: '',
          // Initialize template for custom prompt node
          template: template.category === 'CustomPrompt' ? { content: '' } : undefined,
        },
      };

      setNodes((nds: any) => nds.concat(newNode));
    } catch (error) {
      console.error('Error creating node:', error);
    }
  }, [reactFlowInstance, setNodes]);

  const getNodeType = (category: string) => {
    switch (category) {
      case 'Team':
        return 'teamNode';
      case 'LLM Models':
        return 'llmNode';
      case 'CustomPrompt':
        return 'customPromptNode';
      default:
        return 'teamNode';
    }
  };

  // Load multi-agent data and convert to graph structure
  const loadMultiAgentData = useCallback(async (id: string) => {
    try {
      setIsLoading(true);

      // Load multi-agent data with React Flow data for editing
      const response = await BaseInstance.get(`${TEAMS}/${id}?include_flow_data=true`);
      const multiAgent: MultiAgent = response.data;

      setMultiAgentData(multiAgent);
      setIsEditMode(true);

      // Restore React Flow diagram if saved data exists
      if (multiAgent.react_flow_json && multiAgent.react_flow_json.nodes && multiAgent.react_flow_json.edges) {
        try {
          // Restore nodes and edges from saved data
          const restoredNodes = multiAgent.react_flow_json.nodes.map((node: any) => {
            const restoredData = {
              ...node.data,
              // Ensure all required data properties are present
              label: node.data.label || 'Unnamed Node',
              category: node.data.category || 'Team',
              description: node.data.description || '',
            };

            // For LLM nodes, ensure provider and model data is properly restored
            if (node.type === 'llmNode') {
              if (node.data.provider && node.data.model) {
                restoredData.provider = node.data.provider;
                restoredData.model = node.data.model;
                restoredData.label = `${node.data.provider}:${node.data.model}`;
              } else {
                if (multiAgent.model && multiAgent.model !== NotSelected) {
                  const [provider, model] = multiAgent.model.includes(':')
                    ? multiAgent.model.split(':')
                    : ['Unknown', multiAgent.model];

                  restoredData.provider = provider;
                  restoredData.model = model;
                  restoredData.label = `${provider}:${model}`;
                  restoredData.description = `Model: ${multiAgent.model}`;
                }
              }

              // Ensure API key is restored
              if (!restoredData.apiKey && multiAgent.api_key) {
                restoredData.apiKey = multiAgent.api_key;
              }

              // Ensure models array is restored
              if (node.data.models) {
                restoredData.models = node.data.models;
              }
            }

            // For Team nodes, ensure team data is properly restored
            if (node.type === 'teamNode') {
              restoredData.teamData = multiAgent;
              restoredData.teamName = multiAgent.name;
              // Restore agents if they exist
              if (node.data.agents) {
                restoredData.agents = node.data.agents;
              } else if (multiAgent.agent_ids && multiAgent.agent_ids.length > 0) {
                // Load agent details for the team
                restoredData.agents = []; // Will be populated asynchronously
              }
            }

            // For CustomPrompt nodes, ensure template data is properly restored
            if (node.type === 'customPromptNode') {
              if (node.data.template) {
                restoredData.template = node.data.template;
              } else {
                // Fallback: create empty template if none exists
                restoredData.template = { content: '' };
              }
            }

            return {
              ...node,
              data: restoredData
            };
          });

          const restoredEdges = multiAgent.react_flow_json.edges || [];

          // Set the restored flow data
          setNodes(restoredNodes);
          setEdges(restoredEdges);

          // Load agent details for team nodes that need them
          const loadAgentDetails = async () => {
            const updatedNodes = [...restoredNodes];
            let needsUpdate = false;

            for (let i = 0; i < updatedNodes.length; i++) {
              const node = updatedNodes[i];
              if (node.type === 'teamNode' && multiAgent.agent_ids && multiAgent.agent_ids.length > 0) {
                // Check if agents need to be loaded
                if (!node.data.agents || node.data.agents.length === 0) {
                  try {
                    // Load agent details
                    const agentPromises = multiAgent.agent_ids.map(async (agentId: string) => {
                      try {
                        const response = await BaseInstance.get(`${AGENT}/${agentId}`);
                        return response.data;
                      } catch (error) {
                        console.error(`Failed to load agent ${agentId}:`, error);
                        return null;
                      }
                    });

                    const agents = (await Promise.all(agentPromises)).filter(agent => agent !== null);
                    updatedNodes[i].data.agents = agents;
                    needsUpdate = true;
                  } catch (error) {
                    console.error('Failed to load agent details:', error);
                  }
                }
              }
            }

            if (needsUpdate) {
              // Use requestAnimationFrame to batch the update and reduce ResizeObserver warnings
              requestAnimationFrame(() => {
                setNodes([...updatedNodes]);
              });
            }
          };

          // Load agent details asynchronously
          loadAgentDetails();

          // If we have restored data, we can return early as the diagram is already loaded
          return;
        } catch (error) {
          console.warn('Failed to restore React Flow data, falling back to default conversion:', error);
        }
      }

      // Convert multi-agent data to graph structure (fallback if no saved flow data)
      const graphNodes: Node[] = [];
      const graphEdges: Edge[] = [];

      // Create Team node immediately for better UX
      const teamNode: Node = {
        id: `team-${multiAgent.id}`,
        type: 'teamNode',
        position: { x: 400, y: 200 },
        data: {
          label: multiAgent.name,
          category: 'Team',
          description: multiAgent.description,
          teamName: multiAgent.name,
          teamData: multiAgent,
          agents: [], // Will be populated asynchronously
        }
      };
      graphNodes.push(teamNode);

      // Update nodes immediately to show the team node
      setNodes(graphNodes);
      setEdges([]);

      // Create LLM node if model is specified (non-blocking)
      if (multiAgent.model && multiAgent.model !== NotSelected) {
        const llmNode: Node = {
          id: `llm-${multiAgent.id}`,
          type: 'llmNode',
          position: { x: 100, y: 200 },
          data: {
            label: multiAgent.model,
            category: 'LLM Models',
            description: `Model: ${multiAgent.model}`,
            provider: multiAgent.model.includes(':') ? multiAgent.model.split(':')[0] : 'Unknown',
            model: multiAgent.model.includes(':') ? multiAgent.model.split(':')[1] : multiAgent.model,
            apiKey: multiAgent.api_key || '',
          }
        };
        graphNodes.push(llmNode);

        // Connect LLM to Team
        graphEdges.push({
          id: `edge-llm-${multiAgent.id}`,
          source: `llm-${multiAgent.id}`,
          target: `team-${multiAgent.id}`,
          sourceHandle: 'output',
          targetHandle: 'llm-input',
        });
      }

      // Create CustomPrompt node
      const customPromptNode: Node = {
        id: `custom-prompt-${multiAgent.id}`,
        type: 'customPromptNode',
        position: { x: 250, y: 100 },
        data: {
          label: 'Custom Prompt',
          category: 'CustomPrompt',
          description: 'System prompt for the team',
          template: {
            content: multiAgent.instructions && multiAgent.instructions.length > 0
              ? multiAgent.instructions.join('\n')
              : ''
          }, // Initialize with actual instructions or empty string
        }
      };
      graphNodes.push(customPromptNode);

      // Connect CustomPrompt to Team
      graphEdges.push({
        id: `edge-prompt-${multiAgent.id}`,
        source: `custom-prompt-${multiAgent.id}`,
        target: `team-${multiAgent.id}`,
        sourceHandle: 'output',
        targetHandle: 'prompt-input',
      });

      // Update nodes with all created nodes
      setNodes(graphNodes);
      setEdges(graphEdges);

      // Load agent details asynchronously if agent_ids exist
      if (multiAgent.agent_ids && multiAgent.agent_ids.length > 0) {
        setTimeout(async () => {
          try {
            const agentPromises = multiAgent.agent_ids.map(async (agentId) => {
              try {
                const agentResponse = await BaseInstance.get(`${AGENT}/${agentId}`);
                return { id: agentId, data: agentResponse.data };
              } catch (error) {
                return { id: agentId, data: null };
              }
            });

            const agents = await Promise.all(agentPromises);

            // Update team node with actual agent data
            setNodes((prevNodes: any) =>
              prevNodes.map((node: any) => {
                if (node.type === 'teamNode') {
                  return {
                    ...node,
                    data: {
                      ...node.data,
                      agents: agents.filter(agent => agent.data).map(agent => agent.data),
                    }
                  };
                }
                return node;
              })
            );
          } catch (error) {
            console.error('Failed to load agent details:', error);
          }
        }, 200); // Small delay to let UI render first
      }

      // Update the graph with all nodes and edges
      setNodes(graphNodes);
      setEdges(graphEdges);

    } catch (error: any) {
      dispatch(ShowError(`Failed to load multi-agent: ${error?.message || 'Unknown error'}`));
    } finally {
      setIsLoading(false);
    }
  }, [setNodes, setEdges]);

  // Load multi-agent data when teamId is present
  useEffect(() => {
    if (teamId) {
      // Add timeout to prevent infinite loading
      const timeoutId = setTimeout(() => {
        if (isLoading) {
          dispatch(ShowError('Loading timeout. Please try again.'));
          setIsLoading(false);
        }
      }, 30000); // 30 second timeout

      loadMultiAgentData(teamId);

      return () => clearTimeout(timeoutId);
    }
  }, [teamId, loadMultiAgentData, isLoading, dispatch]);

  // Generate JSON structure for saving
  const generateJsonStructure = useCallback(() => {
    return {
      nodes: nodes.map(node => ({
        id: node.id,
        type: node.type,
        position: node.position,
        data: node.data
      })),
      edges: edges.map(edge => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        sourceHandle: edge.sourceHandle,
        targetHandle: edge.targetHandle
      }))
    };
  }, [nodes, edges]);

  // Convert graph to multi-agent data
  const convertGraphToMultiAgentData = useCallback(() => {
    const teamNode = nodes.find((node: any) => node.type === 'teamNode');
    const llmNode = nodes.find((node: any) => node.type === 'llmNode');

    if (!teamNode) {
      throw new Error('Team node is required');
    }

    const teamData = teamNode.data.teamData || {};
    const teamName = teamNode.data.teamName || '';
    const teamDescription = teamNode.data.description || '';
    const agents = teamNode.data.agents || [];

    // Build multi-agent data
    const apiMultiAgentData: MultiAgent = {
      name: teamName,
      description: teamDescription,
      model: llmNode ? `${llmNode.data.provider}:${llmNode.data.model}` : NotSelected,
      api_key: llmNode ? (llmNode.data.apiKey || '') : (teamData.api_key || ''),
      instructions: teamData.instructions || [],
      show_tool_calls: teamData.show_tool_calls || true,
      markdown: teamData.markdown || true,
      is_shared: teamData.is_shared || false,
      team_mode: teamData.team_mode || 'route',
      agent_ids: agents.map((agent: Agent) => agent.id).filter((id: any) => id && typeof id === 'string' && id.trim() !== ''),
      team_metadata: {
        streaming: teamData.team_metadata?.streaming || true,
        enable_zeasecure: teamData.team_metadata?.enable_zeasecure || false,
        zeasecure_api_key: teamData.team_metadata?.zeasecure_api_key || '',
        user_context_awareness: teamData.team_metadata?.user_context_awareness || false,
      },
      team_members: teamData.team_members || [],
      react_flow_json: generateJsonStructure(), // Include React Flow diagram data
      ...(isEditMode && multiAgentData?.id && typeof multiAgentData.id === 'string' && multiAgentData.id.trim() !== '' && { id: multiAgentData.id })
    };

    return apiMultiAgentData;
  }, [nodes, edges, isEditMode, multiAgentData, generateJsonStructure]);

  // Validate multi-agent data
  const validateMultiAgentData = useCallback((data: MultiAgent) => {
    const errors: string[] = [];

    if (!data.name || data.name.trim() === '') {
      errors.push('Team name is required');
    }

    if (!data.model || data.model === NotSelected) {
      errors.push('LLM model is required');
    }

    if (!data.api_key || data.api_key.trim() === '') {
      errors.push('API key is required');
    }

    if (!data.agent_ids || data.agent_ids.length === 0) {
      errors.push('At least one agent is required');
    }

    return errors;
  }, []);

  // Save multi-agent
  const saveMultiAgent = useCallback(async () => {
    try {
      setIsSaving(true);
      dispatch(StartLoading());

      const multiAgentData = convertGraphToMultiAgentData();
      const validationErrors = validateMultiAgentData(multiAgentData);

      if (validationErrors.length > 0) {
        const errorMessage = validationErrors.join(', ');
        dispatch(ShowError(errorMessage));
        return;
      }

      let response;
      if (isEditMode) {
        response = await BaseInstance.put(`${TEAMS}/${multiAgentData.id}`, multiAgentData);
        dispatch(ShowSuccess('Multi-agent updated successfully!'));
        // Stay on the same page for editing
      } else {
        response = await BaseInstance.post(TEAMS, multiAgentData);
        dispatch(ShowSuccess('Multi-agent created successfully!'));
        // Navigate to edit page with the new team ID
        const createdTeamId = response.data?.id;
        if (createdTeamId) {
          navigate(`/multi-agents/create-multi-agent/${createdTeamId}`);
        } else {
          // Fallback to list page if no ID in response
          navigate('/agents');
        }
      }

    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save multi-agent';
      dispatch(ShowError(errorMessage));
    } finally {
      setIsSaving(false);
      dispatch(StopLoading());
    }
  }, [convertGraphToMultiAgentData, validateMultiAgentData, isEditMode, dispatch, navigate]);

  return (
    <>
      <Container maxWidth={false} sx={{ py: 3, height: "calc(100vh - 81px)", px: 3 }}>
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Breadcrumb */}
          <Breadcrumb
            data={[
              { name: "Multi-Agents", path: "/agents", fromMultiAgentVBuilder: true },
              { name: isEditMode ? `Edit Team: ${multiAgentData?.name || teamId}` : "Create Multi-Agent", path: "" }
            ]}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={saveMultiAgent}
              disabled={isSaving || isLoading}
              startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
              sx={{ textTransform: "none" }}
            >
              {isSaving ? 'Saving...' : (isEditMode ? 'Update Multi-Agent' : 'Create Multi-Agent')}
            </Button>
          </Box>

          {/* Main Content - React Flow Editor */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              backgroundColor: "#f8f9fa",
              borderRadius: 2,
              overflow: "visible",
              border: 1,
              borderColor: "divider",
              mt: 2,
            }}
          >
            {/* Node Palette */}
            <NodePalette />

            {/* React Flow Canvas */}
            <Box sx={{ flex: 1, position: "relative", overflow: "visible" }}>
              <ResizeObserverErrorBoundary>
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onConnect={onConnect}
                  onInit={setReactFlowInstance}
                  onDrop={onDrop}
                  onDragOver={(e) => e.preventDefault()}
                  nodeTypes={nodeTypes}
                  fitView
                  attributionPosition="bottom-left"
                  connectionMode={ConnectionMode.Loose}
                  deleteKeyCode={null}
                  multiSelectionKeyCode={null}
                  selectionKeyCode={null}
                  panOnDrag={true}
                  panOnScroll={true}
                  zoomOnScroll={true}
                  zoomOnPinch={true}
                  preventScrolling={false}
                  style={{
                    // Remove problematic CSS properties that cause clipping
                    width: "100%",
                    height: "100%"
                  }}
                >
                  <Controls />
                  <Background />
                </ReactFlow>
              </ResizeObserverErrorBoundary>
            </Box>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default CreateMultiAgent;
